import * as React from 'react'
import { Button, Container, Alert } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import apiClient from 'api/common/common'
import Footer from 'components/Footer'
import Header from 'components/Header'
import loginData from 'utilities/loginData'
import bpoData from 'utilities/bpoData'

/**
 * BPO審査却下画面
 * BPO審査で却下された場合に表示される画面
 */
// 定数として定義
const MAX_RETRY_COUNT = 2

const BpoRejectedScreen: React.FC = () => {
  const navigate = useNavigate()
  const [failureCount, setFailureCount] = React.useState<number>(-1)
  const [isLoading, setIsLoading] = React.useState<boolean>(true)

  React.useEffect(() => {
    if (!loginData.isLoggedIn()) {
      navigate('/')
      return
    }

    // BPO失敗回数を取得
    const fetchFailureCount = async () => {
      try {
        const response = await apiClient.get('/bpo/failure-count')
        
        if (response.data && typeof response.data.failureCount === 'number') {
          setFailureCount(response.data.failureCount)
        }
      } catch (error: any) {
        // エラー時はデフォルト値を0に設定（再試行を許可）
        setFailureCount(0)
      } finally {
        setIsLoading(false)
      }
    }

    fetchFailureCount()
  }, [navigate])

  /**
   * 本人確認をやり直すボタンのハンドラー
   */
  const handleRetryButton = () => {
    
    // BPOデータをクリア
    bpoData.clearAll()
    
    // 本人確認画面へ戻る
    navigate('/registerIdentificationForIndividual')
  }

  return (
    <>
      <Header title="審査結果" />
      <main>
        <Container className="my-5">
          <Alert variant="danger">
            <Alert.Heading>審査が承認されませんでした</Alert.Heading>
            <p>
              申し訳ございませんが、ご提出いただいた本人確認書類の審査が承認されませんでした。
            </p>
          </Alert>

          {/* ローディング中は何も表示しない */}
          {isLoading ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : failureCount < MAX_RETRY_COUNT ? (
            /* 失敗回数が2回未満の場合は通常の案内 */
            <>
              <div className="note mb-4">
                <div className="mb-2">以下の点をご確認の上、再度お手続きをお願いいたします：</div>
                <ul className="mb-0">
                  <li>本人確認書類が有効期限内であること</li>
                  <li>書類の全体が鮮明に撮影されていること</li>
                  <li>登録情報と書類の記載内容が一致していること</li>
                  <li>対応している本人確認書類であること</li>
                </ul>
              </div>

              <div className="d-grid gap-3">
                <Button 
                  variant="primary" 
                  size="lg"
                  onClick={handleRetryButton}
                >
                  本人確認をやり直す
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="lg"
                  onClick={() => {
                    const requestId = bpoData.getBpoRequestId()
                    navigate(`/contactUs?context=bpo-rejected&referenceId=${requestId}`)
                  }}
                >
                  お問い合わせ
                </Button>
              </div>
            </>
          ) : (
            /* 失敗回数が2回以上の場合は問い合わせのみ */
            <>
              <Alert variant="warning" className="mb-4">
                <Alert.Heading>お問い合わせが必要です</Alert.Heading>
                <p>
                  複数回の審査で承認されなかったため、カスタマーサポートへのお問い合わせが必要です。
                </p>
                <p className="mb-0">
                  お手数をおかけしますが、詳細な確認のため下記よりお問い合わせください。
                </p>
              </Alert>

              <div className="d-grid gap-3">
                <Button 
                  variant="primary" 
                  size="lg"
                  onClick={() => {
                    const requestId = bpoData.getBpoRequestId()
                    navigate(`/contactUs?context=bpo-rejected&referenceId=${requestId}`)
                  }}
                >
                  お問い合わせする
                </Button>
              </div>
            </>
          )}

          <div className="text-center mt-4 text-muted">
            <small>
              ご不明な点がございましたら、カスタマーサポートまでお問い合わせください。
            </small>
          </div>
        </Container>
      </main>
      <Footer />
    </>
  )
}

export default BpoRejectedScreen